import { useState } from 'react';

import { clamp, equals } from 'ramda';
import type { Editor } from 'slate';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useSlateSelector } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import { closeSplitTableCellsPopover } from './split-table-cells-popover-state';

const MINIMUM_SPLIT_COUNT = 1;

/** Word's limit on the columns a split can make, applied to rows as well. */
const MAXIMUM_SPLIT_COUNT = 63;

/** How many columns each selected column divides into by default. */
const COLUMN_SPLIT_FACTOR = 2;

/** The split popover's field values, what the selection allows, and the handlers that change them. */
export function useSplitTableCellsForm(): {
  cancel: () => void;
  canMergeBeforeSplit: boolean;
  canSplitRows: boolean;
  columnCount: number;
  handleColumnCountChange: (columnCount: number | undefined) => void;
  handleMergeBeforeSplitChange: (checked: boolean) => void;
  handleRowCountChange: (rowCount: number | undefined) => void;
  isMergingBeforeSplit: boolean;
  rowCount: number;
  splitCells: () => void;
} {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { editingMode } = useEditingMode();
  const { canMergeBeforeSplit, defaultColumnCount, isVerticallyMerged, selectedRowCount } =
    useSlateSelector(getSplitSelectionState, equals);

  const [editedColumnCount, setEditedColumnCount] = useState<number | null>(null);
  const [requestedRowCount, setRequestedRowCount] = useState(MINIMUM_SPLIT_COUNT);
  const [mergeBeforeSplit, setMergeBeforeSplit] = useState(true);

  const isMergingBeforeSplit = canMergeBeforeSplit && mergeBeforeSplit;
  const columnCount =
    editedColumnCount ?? (isMergingBeforeSplit ? defaultColumnCount : COLUMN_SPLIT_FACTOR);

  /** Rows split within one row, or back into the rows a vertical merge spans. */
  const canSplitRows = selectedRowCount === 1 || isVerticallyMerged;
  const rowCount = canSplitRows ? requestedRowCount : MINIMUM_SPLIT_COUNT;

  return {
    canMergeBeforeSplit,
    canSplitRows,
    cancel,
    columnCount,
    handleColumnCountChange,
    handleMergeBeforeSplitChange,
    handleRowCountChange,
    isMergingBeforeSplit,
    rowCount,
    splitCells,
  };

  function handleColumnCountChange(nextColumnCount: number | undefined): void {
    setEditedColumnCount(clampSplitCount(nextColumnCount));
  }

  function handleMergeBeforeSplitChange(checked: boolean): void {
    setMergeBeforeSplit(checked);
    setEditedColumnCount(null);
  }

  /** A vertically merged selection only unmerges into its own rows, so other counts snap to that. */
  function handleRowCountChange(nextRowCount: number | undefined): void {
    const clampedRowCount = clampSplitCount(nextRowCount);
    if (!isVerticallyMerged) return setRequestedRowCount(clampedRowCount);

    setRequestedRowCount(
      clampedRowCount === MINIMUM_SPLIT_COUNT ? MINIMUM_SPLIT_COUNT : selectedRowCount
    );
  }

  function splitCells(): void {
    closeSplitTableCellsPopover();
    if (!currentUserId || !editingMode) return;

    AuthorizedTransforms.splitTableCells(editor, {
      authorId: currentUserId,
      columnCount,
      editingMode,
      mergeBeforeSplit: isMergingBeforeSplit,
      rowCount,
    });
    ClientTransforms.focus(editor);
  }

  function cancel(): void {
    closeSplitTableCellsPopover();
    ClientTransforms.focus(editor);
  }
}

function clampSplitCount(count: number | undefined): number {
  return clamp(MINIMUM_SPLIT_COUNT, MAXIMUM_SPLIT_COUNT, count ?? MINIMUM_SPLIT_COUNT);
}

function getSplitSelectionState(editor: Editor): {
  canMergeBeforeSplit: boolean;
  defaultColumnCount: number;
  isVerticallyMerged: boolean;
  selectedRowCount: number;
} {
  const canMergeBeforeSplit = Queries.getIsGridSelection(editor);

  const region = Queries.getSelectedGridRegion(editor);
  if (!region)
    return {
      canMergeBeforeSplit,
      defaultColumnCount: COLUMN_SPLIT_FACTOR,
      isVerticallyMerged: false,
      selectedRowCount: MINIMUM_SPLIT_COUNT,
    };

  const { rect, tableEntry } = region;
  const { columnCount, rowCount } = Queries.getGridRectSize(rect);

  return {
    canMergeBeforeSplit,
    defaultColumnCount: columnCount * COLUMN_SPLIT_FACTOR,
    isVerticallyMerged: Queries.getIsVerticallyMergedGridRect(tableEntry, rect),
    selectedRowCount: rowCount,
  };
}
